import { useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'

import { useAddresses } from '@/api/addresses/queries/useAddresses'
import { useUpdateUserPackage } from '@/api/user-package/mutations/useUpdateUserPackage'
import { PkgShippingMethod, UserPkg } from '@/api/user-package/types/UserPkg'
import { AppDialog } from '@/components/AppDialog'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { Button } from '@/components/ui/button'
import { ShippingPrices } from '@/helpers/priceChart'
import { RoutePath } from '@/RoutePath'

import EditAddressDialog from '../Profile/components/EditAddressDialog'
import { AddressSelector } from './components/AddressSelector'
import { OptionCard } from './components/OptionCard'
import { ShippingMethodSelector } from './components/ShippingMethodSelector'
export interface ShippingOptions {
  selectedAddressId: string
  selectedShippingMethod: PkgShippingMethod
  isInsuranceAdded: boolean
}

interface Props {
  pkg: UserPkg
  shippingOptions: ShippingOptions
  onUpdateShippingOptions: (options: Partial<ShippingOptions>) => void
  shippingPrices: ShippingPrices
  insurancePrice: number
}

export default function ShippingPage({ pkg, shippingOptions, onUpdateShippingOptions, shippingPrices, insurancePrice }: Props) {
  const history = useHistory()
  const [showNoInsuranceConfirm, setShowNoInsuranceConfirm] = useState(false)
  const { pkgId } = useParams<{ pkgId: string }>()
  const [showInsuranceModal, setShowInsuranceModal] = useState(false)
  const { mutateAsync: updatePackageMutation, isPending: isUpdatingPackage } = useUpdateUserPackage()
  const { data: addresses } = useAddresses()
  const [showAddAddress, setShowAddAddress] = useState(false)

  const handleContinue = async () => {
    if (!shippingOptions.selectedAddressId && !addresses?.length) {
      setShowAddAddress(true)
      return
    }
    const selectedAddress = addresses?.find(addr => addr.id === shippingOptions.selectedAddressId)
    if (!selectedAddress) {
      setShowAddAddress(true)
      return
    }
    await updatePackageMutation({
      packageId: pkg.id,
      updateParam: {
        shippingAdress_adress: selectedAddress.street,
        shippingAdress_city: selectedAddress.city,
        shippingAdress_postalCode: selectedAddress.postalCode,
        shippingAdress_apartmentNumber: selectedAddress.apartment,
        shippingMethod: shippingOptions.selectedShippingMethod,
        shippingAddressId: selectedAddress.id,
      },
    })
    if (!shippingOptions.isInsuranceAdded) {
      setShowNoInsuranceConfirm(true)
    } else {
      // Navigate to payment summary page
      history.push(`${RoutePath.PaymentSummary}/${pkgId}`)
    }
  }

  const handleAddressCreated = (createdAddress?: { id: string }) => {
    if (createdAddress) {
      onUpdateShippingOptions({ selectedAddressId: createdAddress.id })
    }
  }

  return (
    <div className='space-y-6'>
      {/* Title Section */}
      <div>
        <h1 className='text-text-primary mb-2 text-2xl font-medium'>בחירת משלוח</h1>
        <p className='text-text-secondary text-sm leading-relaxed'>
          נשאר רק לבחור איך לשלוח את החבילה, ואם רוצים להיות רגועים יותר- גם לצרף ביטוח 360°
        </p>
      </div>

      {/* Address Selection */}
      <div className='space-y-4'>
        <h3 className='text-text-primary text-sm font-medium'>בחר כתובת למשלוח</h3>
        <AddressSelector
          onAddAddress={() => setShowAddAddress(true)}
          selectedAddressId={shippingOptions.selectedAddressId}
          onSelectAddress={addressId => onUpdateShippingOptions({ selectedAddressId: addressId })}
        />
      </div>

      {/* Shipping Method Selection */}
      <div className='space-y-4'>
        <h3 className='text-text-primary text-sm font-medium'>בחר סוג משלוח</h3>
        <ShippingMethodSelector
          selectedMethod={shippingOptions.selectedShippingMethod}
          onSelectMethod={method => onUpdateShippingOptions({ selectedShippingMethod: method })}
          packageValue={pkg.valueInUSD || 0}
          shippingPrices={shippingPrices}
        />
      </div>

      {/* Additional Options */}
      <div className='space-y-4'>
        <h3 className='text-text-primary text-sm font-medium'>אופציות נוספות</h3>
        <OptionCard
          onSelect={() => onUpdateShippingOptions({ isInsuranceAdded: !shippingOptions.isInsuranceAdded })}
          selected={shippingOptions.isInsuranceAdded}
          title='רוצה להוסיף ביטוח 360° לחבילה?'
          description={
            <p>
              לקוחות רבים בוחרים להוסיף ביטוח 360° כדי להיות הכי רגועים בעולם.{' '}
              <Button variant='link' className='text-sm font-normal' size='sm' onClick={() => setShowInsuranceModal(true)}>
                למידע נוסף על הביטוח
              </Button>
            </p>
          }
          endContent={insurancePrice > 0 ? <p>${insurancePrice.toFixed(2)}</p> : null}
        />
      </div>

      {/* Continue Button */}
      <div className='space-y-4'>
        <Button onClick={handleContinue} className='w-full' size='lg' loading={isUpdatingPackage}>
          {!shippingOptions.selectedAddressId && !addresses?.length ? 'הוסף כתובת למשלוח' : 'המשך לסיכום ותשלום'}
        </Button>
      </div>

      {/* Insurance Info Modal */}
      <AppDialog isOpen={showInsuranceModal} title='ביטוח 360°' onOpenChange={setShowInsuranceModal}>
        <div className='text-text-secondary space-y-4'>
          <p>למען הסר ספק, שיפטנבול לוקחת אחריות מלאה על חבילות שאבדו מהרגע שנשלחו מהמרכז הלוגיסטי עד שהן מגיעות אליכם באופן אוטומטי.</p>
          <div>
            <p className='font-medium'>הביטוח יאפשר לכם:</p>
            <ul className='mt-2 space-y-1 text-sm'>
              <li>• לבטח את סה״כ העסקה: עלות המוצרים ונזקים שנגרמו לתכולת החבילה מהצד של הספק + דמי המשלוח.</li>
              <li>• החזר מהיר של עד 7 ימי עסקים.</li>
              <li>• החזר כספי לחשבון במקום זיכוי לשימוש חוזר.</li>
            </ul>
          </div>
          <div>
            <p className='font-medium'>שימו לב!</p>
            <p className='text-sm'>לא ניתן לבטח פריטים שבירים. מוצרים אלו נשלחים באריזתן המקורית ואנו מוסיפים רק מעטפת הגנה חיצונית.</p>
          </div>
          <div className='flex flex-col items-center gap-2 pt-6'>
            <p className='text-xs'>
              *בכפוף לתנאי השימוש באתר שלנו.
              <br />
              רוצים לדעת את כל הפרטים?{' '}
              <a
                href='https://www.shiptanbul.com/knowledge-center/faqs/insurance'
                target='_blank'
                rel='noreferrer'
                className='text-text-link hover:underline'
              >
                לחצו כאן.
              </a>
            </p>
            <Button onClick={() => setShowInsuranceModal(false)} variant='outline'>
              סגור
            </Button>
          </div>
        </div>
      </AppDialog>
      <EditAddressDialog isOpen={showAddAddress} onOpenChange={setShowAddAddress} editingAddress={null} onSuccess={handleAddressCreated} />
      <ConfirmDialog
        isOpen={showNoInsuranceConfirm}
        onClose={() => setShowNoInsuranceConfirm(false)}
        onConfirm={() => {
          setShowNoInsuranceConfirm(false)
          history.push(`${RoutePath.PaymentSummary}/${pkgId}`)
        }}
        confirmText='המשך בלי ביטוח'
        cancelText='חזרה'
        title='האם להמשיך בלי ביטוח?'
        content='אנחנו ממליצים להוסיף ביטוח 360° כדי להיות הכי רגועים בעולם. האם אתה בטוח שאתה רוצה להמשיך בלי ביטוח?'
      />
    </div>
  )
}
